import { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import {
    UserPlus,
    Search,
    Filter,
    Users,
    ChevronLeft,
    ChevronRight,
    Shield
} from "lucide-react";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { getUsers, organizationUnits, registerdUsers, sendInvite, userInvites } from "./Api/activedirectoryapi";

interface ADUser {
    id: string;
    name: string;
    organisationUnit: string;
    distinguishedName: string;
    samAccountName: string;
    userPrincipalName: string;
    emailAddress: string;
    verificationCode: string;
    isRegistered: boolean;
}


const ITEMS_PER_PAGE = 10;

export default function InviteAdUsers() {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const userData = useUserStore((state) => state.user);
    const [adUsers, setAdUsers] = useState<ADUser[]>([]);

    // Filter and search logic
    const filteredUsers = useMemo(() => {
        let filtered = adUsers;
        console.log(adUsers)

        // Apply registration filter
        if (filterType === "registered") {
            filtered = filtered.filter(user => user.isRegistered);
        } else if (filterType === "unregistered") {
            filtered = filtered.filter(user => !user.isRegistered);
        }

        // Apply search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(searchLower) ||
                user.samAccountName.toLowerCase().includes(searchLower) ||
                user.emailAddress.toLowerCase().includes(searchLower) ||
                user.userPrincipalName.toLowerCase().includes(searchLower)
            );
        }

        return filtered;
    }, [searchTerm, filterType, adUsers]);


    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleFilterChange = (value: string) => {
        setFilterType(value);
        setCurrentPage(1);
    };

    const handleSelectUser = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        const currentPageUserIds = paginatedUsers.map(user => user.id);
        if (checked) {
            // Add all current page users to selection (preserve other page selections)
            setSelectedUsers(prev => {
                const combined = [...prev, ...currentPageUserIds];
                return Array.from(new Set(combined));
            });
        } else {
            // Remove all current page users from selection (preserve other page selections)
            setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
        }
    };

    const handleInviteUser = async (user: ADUser) => {
        if (user.isRegistered) {
            console.log("User already registered:", user.name);
            return;
        }

        try {
            const fullName = user.name;
            const spaceIndex = fullName.indexOf(" ");
            const firstName = fullName.substring(0, spaceIndex);
            const lastName = fullName.substring(spaceIndex + 1);
            let payload = {
                firstName: firstName,
                lastName: lastName,
                email: user.emailAddress,
                tenantName: tenantName,
            };
            await sendInvite(payload);
        } catch (e) {

        }



        console.log("Inviting user:", user.name);
        // Here you would implement the invite logic
    };

    const handleBulkInvite = () => {
        const usersToInvite = selectedUsers
            .map((userId: string) => adUsers.find((u: any) => u.id === userId))
            .filter(
                (user: any) =>
                    user &&
                    !user.isRegistered &&
                    user.emailAddress
            );

        if (usersToInvite.length === 0) {
            console.log("No eligible users to invite.");
            return;
        }

        const invitePromises = usersToInvite.map((user: any) => {
            const fullName = user.name || "";
            const spaceIndex = fullName.indexOf(" ");
            const firstName = spaceIndex !== -1 ? fullName.substring(0, spaceIndex) : fullName;
            const lastName = spaceIndex !== -1 ? fullName.substring(spaceIndex + 1) : "";

            const payload = {
                firstName,
                lastName,
                email: user.emailAddress,
                tenantName: tenantName,
            };

            return sendInvite(payload);
        });

        console.log("Bulk inviting unregistered users:", usersToInvite);

        Promise.all(invitePromises)
            .then(() => {
                console.log("Bulk invite successful");
            })
            .catch((error) => {
                console.error("Error during bulk invite:", error);
            });
    };


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };



    const combineData = (
        units: any[],
        users: any[],
        registeredUsers: any[],
        invitedUsers: any[],
    ): any[] => {
        return users.map((user: any): any => {
            const matchedUnit = units.find((unit: any) =>
                user.DistinguishedName?.includes(unit.distinguishedName)
            );

            const registeredUser = user.EmailAddress
                ? registeredUsers.find(
                    (reg: any) => reg.email?.toLowerCase() === user.EmailAddress?.toLowerCase()
                )
                : null;

            const invitedUser = user.EmailAddress
                ? invitedUsers.find(
                    (inv: any) => inv.email?.toLowerCase() === user.EmailAddress?.toLowerCase()
                )
                : null;

            return {
                id: user.SamAccountName || user.UserPrincipalName || user.DistinguishedName,
                name: user.Name || "",
                organisationUnit: matchedUnit ? matchedUnit.name : "",
                distinguishedName: user.DistinguishedName || "",
                samAccountName: user.SamAccountName || "",
                userPrincipalName: user.UserPrincipalName || "",
                emailAddress: user.EmailAddress || "",
                verificationCode: invitedUser?.verificationCode || "",
                isRegistered: !!registeredUser,
            };
        });
    };



    const fetchDetails = async () => {
        const [units, users, registeredUsers, invitedUsers] = await Promise.all(
            [
                organizationUnits(tenantName),
                getUsers(tenantName),
                registerdUsers(tenantName),
                userInvites(tenantName),
            ],
        );
        const combinedData = combineData(
            units,
            users,
            registeredUsers,
            invitedUsers,
        );
        setAdUsers(combinedData);
    }

    useEffect(() => {
        fetchDetails();
    }, [tenantName])



    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-ad-users-title">
                            Active Directory Users
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Manage and invite Active Directory users to the platform
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleBulkInvite}
                    disabled={selectedUsers.filter(userId => {
                        const user = adUsers.find(u => u.id === userId);
                        return user && !user.isRegistered;
                    }).length === 0}
                    className="gap-2"
                    data-testid="button-invite-selected"
                >
                    <UserPlus className="w-4 h-4" />
                    Invite Now ({selectedUsers.filter(userId => {
                        const user = adUsers.find(u => u.id === userId);
                        return user && !user.isRegistered;
                    }).length})
                </Button>
            </div>

            {/* Main Content */}
            <Card className="border-border/40">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Active Directory Users ({paginatedUsers.length})
                        </CardTitle>
                        <div className="flex items-center gap-4">
                            <Select value={filterType} onValueChange={handleFilterChange}>
                                <SelectTrigger className="w-48" data-testid="select-filter">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Show All</SelectItem>
                                    <SelectItem value="registered">Registered Users</SelectItem>
                                    <SelectItem value="unregistered">Unregistered Users</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="relative w-64">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="pl-10"
                                    data-testid="input-search-ad-users"
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    {filteredUsers.length > 0 ? (
                        <div className="border-t">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/30">
                                        <TableHead className="w-12">
                                            <Checkbox
                                                checked={paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.includes(user.id))}
                                                onCheckedChange={handleSelectAll}
                                                data-testid="checkbox-select-all"
                                            />
                                        </TableHead>
                                        <TableHead className="font-semibold">Name</TableHead>
                                        <TableHead className="font-semibold">User AD Details</TableHead>
                                        <TableHead className="font-semibold">EmailAddress</TableHead>
                                        <TableHead className="font-semibold text-center">Verification Code</TableHead>
                                        <TableHead className="font-semibold text-center">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedUsers.map((user) => (
                                        <TableRow key={user.id} className="hover:bg-muted/20">
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedUsers.includes(user.id)}
                                                    onCheckedChange={() => handleSelectUser(user.id)}
                                                    data-testid={`checkbox-user-${user.id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium" data-testid={`name-${user.id}`}>
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{user.name}</span>
                                                    {user.isRegistered && (
                                                        <Badge className="text-xs w-fit mt-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                            Registered
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell data-testid={`ad-details-${user.id}`} className="max-w-md">
                                                <div className="space-y-1 text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-muted-foreground font-medium min-w-[60px]">OU:</span>
                                                        <span className="text-foreground">{user.organisationUnit}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-muted-foreground font-medium min-w-[60px]">SAM:</span>
                                                        <span className="font-mono text-foreground">{user.samAccountName}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-muted-foreground font-medium min-w-[60px]">UPN:</span>
                                                        <span className="text-foreground">{user.userPrincipalName}</span>
                                                    </div>
                                                    <div className="pt-1 border-t border-border/30">
                                                        <span className="text-xs font-mono text-muted-foreground break-all leading-relaxed" title={user.distinguishedName}>
                                                            {user.distinguishedName}
                                                        </span>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell data-testid={`email-${user.id}`}>
                                                <span className="text-sm">{user.emailAddress}</span>
                                            </TableCell>
                                            <TableCell className="text-center" data-testid={`verification-${user.id}`}>
                                                <div className="flex items-center justify-center">
                                                    <Shield className="w-4 h-4 text-muted-foreground" />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleInviteUser(user)}
                                                    disabled={user.isRegistered}
                                                    className="gap-1"
                                                    data-testid={`button-invite-${user.id}`}
                                                >
                                                    <UserPlus className="w-3 h-3" />
                                                    {user.isRegistered ? 'Invited' : 'Invite'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="p-12 text-center border-t">
                            <div className="text-muted-foreground">
                                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <h3 className="text-lg font-medium mb-2">
                                    {searchTerm ? 'No matching users found' : 'No AD users available'}
                                </h3>
                                <p>
                                    {searchTerm
                                        ? 'Try adjusting your search criteria or filter settings'
                                        : 'Connect to Active Directory to sync user data'
                                    }
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t bg-muted/20">
                            <div className="text-sm text-muted-foreground">
                                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1}
                                    className="gap-1"
                                    data-testid="button-prev-page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = i + 1;
                                        return (
                                            <Button
                                                key={page}
                                                variant={currentPage === page ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handlePageChange(page)}
                                                data-testid={`button-page-${page}`}
                                            >
                                                {page}
                                            </Button>
                                        );
                                    })}
                                    {totalPages > 5 && (
                                        <>
                                            <span className="text-muted-foreground px-2">...</span>
                                            <Button
                                                variant={currentPage === totalPages ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handlePageChange(totalPages)}
                                                data-testid={`button-page-${totalPages}`}
                                            >
                                                {totalPages}
                                            </Button>
                                        </>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages}
                                    className="gap-1"
                                    data-testid="button-next-page"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}